/**
 * One plain sentence per chart, saying what the picture shows — so a reader who does not read
 * charts still leaves with the point. Every sentence is arithmetic on the figures the product sent
 * (who is largest, what share, how much it moved); none of them guesses at a cause.
 */
const pct = (x) => `${Math.round(x * 100)}%`
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v))

/** "Nigeria leads with 96 — 24% of the total; the top 3 make up 58%." */
export function leaderInsight(items, fmt) {
  const list = items.map(i => ({ name: i.name, value: num(i.value) })).filter(i => i.value > 0).sort((a, b) => b.value - a.value)
  if (list.length < 2) return null
  const total = list.reduce((a, i) => a + i.value, 0)
  let s = `${list[0].name} leads with ${fmt(list[0].value)} — ${pct(list[0].value / total)} of the total`
  if (list.length >= 5) s += `; the top 3 make up ${pct(list.slice(0, 3).reduce((a, i) => a + i.value, 0) / total)}`
  return `${s}.`
}

/** "East & Southern Africa is the largest region: 41%, from 5 countries." */
export function regionInsight(groups) {
  const real = groups.filter(g => g.key !== 'OTHER')
  if (real.length < 2) return null
  const total = groups.reduce((a, g) => a + g.total, 0)
  const g = real[0]
  return `${g.name} is the largest region: ${pct(g.total / total)}, from ${g.items.length} ${g.items.length === 1 ? 'country' : 'countries'}.`
}

/** "Questions rose 365% from May to Oct, peaking in Sep (104)." — for the first series that has
 *  two or more points. */
export function trendInsight(labels, series, fmt) {
  for (const s of series) {
    const pts = labels.map((l, i) => ({ l: String(l), v: num(s.values[i]) })).filter(p => p.v !== null)
    if (pts.length < 2) continue
    const first = pts[0], last = pts[pts.length - 1]
    const peak = pts.reduce((m, p) => (p.v > m.v ? p : m), pts[0])
    let move
    if (first.v === 0) move = last.v > 0 ? `grew from 0 to ${fmt(last.v)} by ${last.l}` : 'stayed at 0'
    else {
      const ch = (last.v - first.v) / Math.abs(first.v)
      move = Math.abs(ch) < 0.02 ? `held steady at about ${fmt(last.v)}` : `${ch > 0 ? 'rose' : 'fell'} ${pct(Math.abs(ch))} from ${first.l} to ${last.l}`
    }
    const peakTxt = peak !== first && peak !== last ? `, peaking in ${peak.l} (${fmt(peak.v)})` : ''
    return `${s.name || 'The figure'} ${move}${peakTxt}.`
  }
  return null
}

/** "East & Southern Africa was #1 in 5 of 6 periods; Asia & the Pacific climbed from #3 to #2." */
export function rankInsight(labels, series) {
  if (series.length < 3 || labels.length < 2) return null
  const rankAt = (i) => {
    const present = series.map(s => ({ n: s.name, v: num(s.values[i]) })).filter(x => x.v !== null).sort((a, b) => b.v - a.v)
    const r = {}
    present.forEach((x, k) => { r[x.n] = k + 1 })
    return r
  }
  const ranks = labels.map((_, i) => rankAt(i))
  const firsts = {}
  ranks.forEach(r => Object.entries(r).forEach(([n, k]) => { if (k === 1) firsts[n] = (firsts[n] || 0) + 1 }))
  const leader = Object.entries(firsts).sort((a, b) => b[1] - a[1])[0]
  if (!leader) return null
  let s = `${leader[0]} was #1 in ${leader[1]} of ${labels.length} periods`
  const a = ranks[0], b = ranks[ranks.length - 1]
  const climb = Object.keys(b).filter(n => a[n]).map(n => [n, a[n] - b[n]]).sort((x, y) => y[1] - x[1])[0]
  if (climb && climb[1] > 0) s += `; ${climb[0]} climbed from #${a[climb[0]]} to #${b[climb[0]]}`
  return `${s}.`
}

/** "Domestic public is the largest part: 52%." */
export function shareInsight(pieces) {
  const list = pieces.filter(p => num(p.value) > 0)
  if (list.length < 2) return null
  const total = list.reduce((a, p) => a + Number(p.value), 0)
  const top = [...list].sort((a, b) => b.value - a.value)[0]
  return `${top.name} is the largest part: ${pct(top.value / total)}.`
}

/** For a scale of ratings (labels 1..N, N up to 10, each with a count): "Average rating 3.9 out of
 *  5, from 133 answers; the most common is 4." Null for anything that is not such a scale. */
export function ratingInsight(labels, values, seriesName) {
  const ls = labels.map(l => String(l).trim())
  if (ls.length < 3 || ls.length > 11 || !ls.every(l => /^\d{1,2}$/.test(l))) return null
  const ns = ls.map(Number)
  if (!ns.every((n, i) => i === 0 || n === ns[i - 1] + 1) || ns[0] > 1) return null
  const vs = values.map(num)
  const total = vs.reduce((a, v) => a + (v || 0), 0)
  if (!(total > 0)) return null
  const mean = ns.reduce((a, n, i) => a + n * (vs[i] || 0), 0) / total
  const mode = ns[vs.indexOf(Math.max(...vs.map(v => v || 0)))]
  const what = seriesName ? ` ${String(seriesName).toLowerCase()}` : ''
  return `Average rating ${mean.toFixed(1)} out of ${ns[ns.length - 1]}, from ${total.toLocaleString('en-US')}${what}; the most common is ${mode}.`
}
